import { queryClient } from "@/app/query";

/**
 * Every read that summarizes executions without living under `/executions`.
 *
 * These are the "Recent executions" / activity surfaces — a flow's Overview, a
 * deployment's Overview, the workspace dashboard — and they are served by
 * `.../stats` and `/dashboard`, not by the executions collection. Until this
 * existed they were the one execution view the realtime stream did NOT refresh:
 * `invalidateExecutions` matched the URL prefix `/api/v1/executions`, the
 * Executions page updated live, and the identical table one click away on the
 * deployment page sat still until you reloaded it.
 *
 * The trigger `.../stats` endpoints are here too — a firing's stats tile counts
 * how its runs ended, so it moves when the run does, not when the event lands.
 *
 * Anchored (`^`…`$`) rather than prefix-matched: `/api/v1/deployments` alone is
 * the deployment LIST, which no execution changes, and refetching it on every
 * status transition of a busy workspace is pure noise.
 */
const EXECUTION_SUMMARY_KEY =
    /^\/api\/v1\/(dashboard|(flows|deployments|webhooks|schedules|event-sources)\/[^/]+\/stats)$/;

/**
 * Broad invalidation for anything execution-related — same predicate pattern
 * as flows' `invalidateFlowVersionState` (see ui/CLAUDE.md gotchas): a
 * Replay/Cancel action must refresh both the list (status chip) and this
 * execution's own detail query, and predicate-matching on the URL prefix
 * covers both without listing every exact key by hand.
 */
export function invalidateExecutions() {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey[0];
            if (typeof key !== "string") return false;
            return key.startsWith("/api/v1/executions") || EXECUTION_SUMMARY_KEY.test(key);
        },
    });
}
